import type { TFunction } from "i18next";

/**
 * Cuánto lleva abierto un caso, en palabras que se leen de un vistazo.
 *
 * ── Por qué no se dicen siempre los minutos ────────────────────────────────
 * «Caso abierto hace 156 min» obliga a dividir entre sesenta para entenderlo, y
 * quien recorre una lista de casos está decidiendo a qué atender primero: la
 * unidad tiene que ser la que corresponde a la escala, no la que salió del
 * cálculo.
 *
 * Y con un caso viejo se vuelve absurdo: «abierto hace 43 200 min» no le dice
 * nada a nadie; «hace 30 días» sí.
 *
 * ── La escala ──────────────────────────────────────────────────────────────
 *   < 1 h    → minutos, exactos. Es la franja donde cada minuto importa.
 *   < 24 h   → horas y minutos. «2 h 36 min» sigue siendo accionable.
 *   < 30 d   → días. A esa altura los minutos son ruido.
 *   >= 30 d  → meses.
 *
 * Los minutos se sueltan al pasar de un día a propósito: un caso de tres días
 * no se atiende distinto por catorce minutos, y arrastrarlos sólo alarga la
 * línea.
 *
 * ── Por qué recibe `t` en vez de devolver una clave ────────────────────────
 * Porque el plural cambia por idioma —«1 día» / «2 días», «1 month» / «2
 * months»— y eso lo resuelve i18next con `count`. Devolver una clave obligaría
 * a cada sitio a acordarse de pasar el conteo, y alguno se olvidaría.
 */
export function lapso(min: number, t: TFunction): string {
  // Defensivo: un reloj desfasado entre el equipo y el servidor puede dar un
  // negativo, y «hace -3 min» es peor que no decir nada.
  const minutos = Math.max(0, Math.floor(min));

  if (minutos < 60) {
    return t("monitoreoIa.lapso.minutos", { defaultValue: "{{count}} min", count: minutos });
  }

  const horas = Math.floor(minutos / 60);

  if (horas < 24) {
    const resto = minutos % 60;
    return resto === 0
      ? t("monitoreoIa.lapso.horas", { defaultValue: "{{count}} h", count: horas })
      : t("monitoreoIa.lapso.horasMinutos", {
          defaultValue: "{{horas}} h {{minutos}} min",
          horas,
          minutos: resto,
        });
  }

  const dias = Math.floor(horas / 24);

  if (dias < 30) {
    return t("monitoreoIa.lapso.dias", { defaultValue: "{{count}} días", count: dias });
  }

  // Meses de treinta días: no es un calendario, es una escala de lectura. Un
  // caso con dos meses abiertos ya no necesita precisión, necesita alarmar.
  const meses = Math.floor(dias / 30);
  return t("monitoreoIa.lapso.meses", { defaultValue: "{{count}} meses", count: meses });
}

export default lapso;
